import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { viewInvitation, requestInvitationViewOTP, verifyInvitationViewOTP, peekInvitation, trackInvitationView } from '../api/game';
import './InvitationLetter.css';
import { sanitizePhoneInput, toEnglishDigits } from '../utils/phone';

// ── تاریخ تولد شمسی ──────────────────────────────────────────
const JALALI_MONTHS = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];

// تبدیل میلادی به شمسی — بدون وابستگی به Intl.
// قبلاً این تبدیل با Intl.DateTimeFormat('en-US-u-ca-persian', ...) انجام می‌شد، اما
// تقویم «persian» در Intl روی برخی مرورگرهای موبایل/وب‌ویوهای اندرویدی (مثل مرورگر
// داخلی تلگرام/اینستاگرام با ICU محدود) پشتیبانی نمی‌شود؛ در آن حالت خروجی NaN می‌شد و
// Array.from({length: NaN}) یک آرایه‌ی خالی می‌ساخت، یعنی فهرست «سال» در فرم تاریخ
// تولد کاملاً خالی می‌ماند (روز و ماه چون هاردکد/مستقل از Intl بودند مشکلی نداشتند).
// این پیاده‌سازی الگوریتم استاندارد و محاسباتی جلالی است که روی همه‌ی مرورگرها یکسان کار می‌کند.
function gregorianToJalali(gy, gm, gd) {
  const g_d_m = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
  let jy;
  if (gy > 1600) {
    jy = 979;
    gy -= 1600;
  } else {
    jy = 0;
    gy -= 621;
  }
  const gy2 = gm > 2 ? gy + 1 : gy;
  let days =
    365 * gy +
    Math.floor((gy2 + 3) / 4) -
    Math.floor((gy2 + 99) / 100) +
    Math.floor((gy2 + 399) / 400) -
    80 +
    gd +
    g_d_m[gm - 1];
  jy += 33 * Math.floor(days / 12053);
  days %= 12053;
  jy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    jy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  let jm, jd;
  if (days < 186) {
    jm = 1 + Math.floor(days / 31);
    jd = 1 + (days % 31);
  } else {
    jm = 7 + Math.floor((days - 186) / 30);
    jd = 1 + ((days - 186) % 30);
  }
  return { year: jy, month: jm, day: jd };
}

// سقف فهرست سال تولد باید همیشه سال شمسی جاری باشد، نه عددی هاردکد (که با گذر زمان
// اجازه‌ی انتخاب تاریخ تولد در آینده را می‌داد).
const _nowForYear = new Date();
const CURRENT_JALALI_YEAR = gregorianToJalali(
  _nowForYear.getFullYear(),
  _nowForYear.getMonth() + 1,
  _nowForYear.getDate()
).year;
const JALALI_YEARS = Array.from({ length: CURRENT_JALALI_YEAR - 1300 + 1 }, (_, i) => CURRENT_JALALI_YEAR - i);

function jalaliDaysInMonth(jm) {
  if (!jm) return 31;
  const m = Number(jm);
  if (m >= 1 && m <= 6) return 31;
  if (m >= 7 && m <= 11) return 30;
  return 30; // اسفند — اجازه‌ی انتخاب روز ۳۰ همیشه داده می‌شود، اعتبارسنجی نهایی سمت سرور انجام می‌شود
}

function isJalaliLeapYear(y) {
  const r = y % 33;
  return [1, 5, 9, 13, 17, 22, 26, 30].includes(r);
}

function getJalaliDaysInMonth(year, month) {
  if (month >= 1 && month <= 6) return 31;
  if (month >= 7 && month <= 11) return 30;
  if (month === 12) {
    return isJalaliLeapYear(year) ? 30 : 29;
  }
  return 30;
}

function getCurrentJalaliDate() {
  const now = new Date();
  return gregorianToJalali(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

function getExactAge(jy, jm, jd) {
  if (!jy || !jm || !jd) return null;
  const birthYear = Number(jy);
  const birthMonth = Number(jm);
  const birthDay = Number(jd);
  
  const today = getCurrentJalaliDate();
  
  let years = today.year - birthYear;
  let months = today.month - birthMonth;
  let days = today.day - birthDay;
  
  if (days < 0) {
    let prevMonth = today.month - 1;
    let prevYear = today.year;
    if (prevMonth === 0) {
      prevMonth = 12;
      prevYear = today.year - 1;
    }
    days += getJalaliDaysInMonth(prevYear, prevMonth);
    months--;
  }
  
  if (months < 0) {
    months += 12;
    years--;
  }
  
  return { years, months, days };
}

function getRemainingTimeToBirthday(jy, jm, jd) {
  if (!jy || !jm || !jd) return null;
  const birthMonth = Number(jm);
  const birthDay = Number(jd);
  
  const today = getCurrentJalaliDate();
  
  let nextBirthdayYear = today.year;
  if (today.month > birthMonth || (today.month === birthMonth && today.day > birthDay)) {
    nextBirthdayYear = today.year + 1;
  }
  
  const daysInBthMonth = getJalaliDaysInMonth(nextBirthdayYear, birthMonth);
  const actualBirthDay = Math.min(birthDay, daysInBthMonth);
  
  let years = nextBirthdayYear - today.year;
  let months = birthMonth - today.month;
  let days = actualBirthDay - today.day;

  if (days < 0) {
    let prevMonth = birthMonth - 1;
    let prevYear = nextBirthdayYear;
    if (prevMonth === 0) {
      prevMonth = 12;
      prevYear = nextBirthdayYear - 1;
    }
    days += getJalaliDaysInMonth(prevYear, prevMonth);
    months--;
  }

  if (months < 0) {
    months += 12;
    years--;
  }

  return { years, months, days };
}

function formatDeparture(iso) {
  if (!iso) return 'جمعه، ساعت ۱۱:۰۰';
  const d = new Date(iso);
  return d.toLocaleDateString('fa-IR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export default function InvitationLetter() {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [phone, setPhone] = useState('');
  const [jy, setJy] = useState('');
  const [jm, setJm] = useState('');
  const [jd, setJd] = useState('');
  const [otp, setOtp] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const exactAge = (jy && jm && jd) ? getExactAge(jy, jm, jd) : null;
  const remainingToBirthday = (jy && jm && jd) ? getRemainingTimeToBirthday(jy, jm, jd) : null;
  const isNearBirthday = remainingToBirthday ? (remainingToBirthday.years === 0 && remainingToBirthday.months === 0) : false;

  // 'preview'  → پیش‌نمایش blank invite (بدون شماره) — قبل از OTP
  // 'phone'    → مرحله‌ی اول (کد + شماره) → ارسال OTP
  // 'otp'      → تأیید کد پیامکی
  // 'name'     → ورود نام و نام خانوادگی (فقط برای blank invite بدون نام از قبل)
  // 'birthdate'→ فقط وقتی سرور اعلام کند که تاریخ تولد ثبت نشده
  const [step, setStep] = useState('phone');
  const [peekData, setPeekData] = useState(null); // اطلاعات پیش‌نمایش
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inv, setInv] = useState(null);
  const [opened, setOpened] = useState(false);
  const viewTracked = useRef(false);

  useEffect(() => {
    const c = searchParams.get('code');
    if (c) setCode(c);
  }, [searchParams]);

  // ثبت اینکه گیرنده لینک دعوت را باز کرده + دریافت peek برای تشخیص blank invite
  useEffect(() => {
    const c = searchParams.get('code');
    if (!c) return;
    if (!viewTracked.current) {
      viewTracked.current = true;
      trackInvitationView(c).catch(() => {});
    }
    // peek: اگر blank invite بود (isBlankInvite=true)، مستقیم به 'preview' می‌رویم
    peekInvitation(c).then((res) => {
      if (res.data?.success) {
        const d = res.data;
        setPeekData(d);
        if (d.isBlankInvite) {
          setStep('preview');
        }
        // اگر نام از قبل روی دعوت‌نامه بود، پر کنیم (برای نمایش)
        if (d.recipientFirstName) setFirstName(d.recipientFirstName);
        if (d.recipientLastName) setLastName(d.recipientLastName);
      }
    }).catch(() => {});
  }, [searchParams]);

  // ── مرحله‌ی اول: کد + شماره تماس → ارسال کد تأیید پیامکی ─────────────
  // صرفاً دانستن شماره تماس گیرنده کافی نیست (ممکن است شخص دیگری آن را بداند)،
  // پس به‌جای نمایش مستقیم نامه، ابتدا یک OTP به همین شماره ارسال می‌شود.
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!code.trim()) { setError('کد دعوت را وارد کنید'); return; }
    if (!phone.trim()) { setError('شماره تماس خود را وارد کنید'); return; }
    setLoading(true);
    try {
      const res = await requestInvitationViewOTP(code.trim(), phone.trim());
      if (res.data?.success) {
        setStep('otp');
      } else {
        setError(res.data?.message || 'ارسال کد تأیید ناموفق بود');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ارسال کد تأیید');
    } finally {
      setLoading(false);
    }
  };

  // ── مرحله‌ی دوم: تأیید کد پیامکی ──────────────────────────────────────
  // بعد از تأیید موفق، سرور این دعوت‌نامه را برای همیشه به این شماره قفل
  // می‌کند (claimedAt) و اعلام می‌کند که آیا تاریخ تولد هم لازم است یا نه.
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!otp.trim()) { setError('کد تأیید را وارد کنید'); return; }
    setLoading(true);
    try {
      const res = await verifyInvitationViewOTP(code.trim(), phone.trim(), otp.trim());
      if (!res.data?.success) {
        setError(res.data?.message || 'کد تأیید نادرست است');
        return;
      }
      if (res.data.needsName) {
        // blank invite بدون نام — باید نام و نام خانوادگی وارد شود
        setStep('name');
      } else if (res.data.needsBirthDate) {
        setStep('birthdate');
      } else {
        const viewRes = await viewInvitation(code.trim(), phone.trim(), {});
        if (viewRes.data?.success) {
          setInv(viewRes.data.invitation);
        } else {
          setError(viewRes.data?.message || 'خطا در دریافت دعوت‌نامه');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در تأیید کد');
    } finally {
      setLoading(false);
    }
  };

  // ارسال دوباره‌ی کد تأیید — همان درخواست مرحله‌ی اول
  const handleResendOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await requestInvitationViewOTP(code.trim(), phone.trim());
      if (!res.data?.success) setError(res.data?.message || 'ارسال کد تأیید ناموفق بود');
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ارسال کد تأیید');
    } finally {
      setLoading(false);
    }
  };

  // ── مرحله‌ی سوم: تاریخ تولد (فقط وقتی سرور آن را الزامی اعلام کرده) ──
  const handleBirthDateSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!jy || !jm || !jd) { setError('تاریخ تولد را کامل وارد کنید'); return; }
    setShowConfirmModal(true);
  };

  // ── مرحله‌ی چهارم: ثبت نام برای blank invite بدون نام ──────────────────
  const handleNameSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!firstName.trim()) { setError('نام را وارد کنید'); return; }
    if (!lastName.trim()) { setError('نام خانوادگی را وارد کنید'); return; }
    setLoading(true);
    try {
      // viewInvitation را با نام صدا می‌زنیم؛ اگر تاریخ تولد هم لازم باشد، به 'birthdate' می‌رویم
      const res = await viewInvitation(code.trim(), phone.trim(), {}, { firstName: firstName.trim(), lastName: lastName.trim() });
      if (res.data?.success) {
        setInv(res.data.invitation);
      } else if (res.data?.requiredStep === 'birth_date' || res.data?.needsBirthDate) {
        setStep('birthdate');
      } else {
        setError(res.data?.message || 'خطا در ثبت نام');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ثبت نام');
    } finally {
      setLoading(false);
    }
  };

  const performBirthDateSubmit = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    try {
      // نام هم ارسال می‌شود تا blank invite‌هایی که در مرحله‌ی 'name' بودند هم پوشش داده شوند
      const res = await viewInvitation(code.trim(), phone.trim(), {
        jy: Number(jy), jm: Number(jm), jd: Number(jd)
      }, { firstName: firstName.trim() || undefined, lastName: lastName.trim() || undefined });
      if (res.data?.success) {
        setInv(res.data.invitation);
      } else {
        setError(res.data?.message || 'تاریخ تولد را به‌درستی وارد کنید');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ثبت تاریخ تولد');
    } finally {
      setLoading(false);
    }
  };

  // ── Phone gate ──────────────────────────────────────────────
  if (!inv) {
    return (
      <div className="ilv-root">
        <div className="ilv-stars" aria-hidden="true">
          {Array.from({ length: 40 }).map((_, i) => (
            <span key={i} style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, animationDelay: `${(Math.random() * 4).toFixed(1)}s` }} />
          ))}
        </div>
        <div className="ilv-gate">
          <div className="ilv-gate-seal">🦉</div>
          {step === 'preview' ? (
            <>
              <h1 className="ilv-gate-title">دعوت‌نامه‌ی جادویی</h1>
              <p className="ilv-gate-sub">
                یک جغد این دعوت‌نامه را برایت آورده است.<br />
                {peekData?.departureDate && (
                  <>قطار در تاریخ <strong>{formatDeparture(peekData.departureDate)}</strong> از سکوی <strong>{peekData.platform || '۹ و ۳/۴'}</strong> حرکت می‌کند.</>
                )}
              </p>
              {error && <div className="ilv-error">{error}</div>}
              <button
                className="ilv-unlock-btn"
                type="button"
                onClick={() => setStep('phone')}
              >
                🦉 می‌خواهم وارد شوم
              </button>
              <p className="ilv-gate-hint">برای دریافت دعوت‌نامه، شماره تماس خود را تأیید کن.</p>
            </>
          ) : step === 'phone' ? (
            <>
              <h1 className="ilv-gate-title">دعوت‌نامه‌ی محرمانه</h1>
              <p className="ilv-gate-sub">
                جغدی نامه‌ای برایت آورده است. برای گشودن دعوت‌نامه‌ی شخصی‌ات،
                شماره‌ای را که نامه به آن فرستاده شده وارد کن.
              </p>
              <form onSubmit={handlePhoneSubmit} className="ilv-gate-form">
                {!searchParams.get('code') && (
                  <input
                    className="ilv-input"
                    placeholder="کد دعوت"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    dir="ltr"
                  />
                )}
                <input
                  className="ilv-input"
                  placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                  value={phone}
                  onChange={(e) => setPhone(sanitizePhoneInput(e.target.value))}
                  dir="ltr"
                  inputMode="tel"
                />

                {error && <div className="ilv-error">{error}</div>}
                <button className="ilv-unlock-btn" type="submit" disabled={loading}>
                  {loading ? 'در حال ارسال...' : '📩 ارسال کد تأیید'}
                </button>
              </form>
              <p className="ilv-gate-hint">فقط دارنده‌ی این شماره می‌تواند دعوت‌نامه را ببیند.</p>
            </>
          ) : step === 'otp' ? (
            <>
              <h1 className="ilv-gate-title">کد تأیید را وارد کن</h1>
              <p className="ilv-gate-sub">
                کد تأیید ۶ رقمی به شماره‌ی <strong dir="ltr">{phone}</strong> پیامک شد.
                برای اینکه فقط خودت بتوانی این دعوت‌نامه‌ی محرمانه را بخوانی، آن را اینجا وارد کن.
              </p>
              <form onSubmit={handleOtpSubmit} className="ilv-gate-form">
                <input
                  className="ilv-input"
                  placeholder="کد تأیید"
                  value={otp}
                  onChange={(e) => setOtp(toEnglishDigits(e.target.value).replace(/\D/g, '').slice(0, 6))}
                  dir="ltr"
                  inputMode="numeric"
                  maxLength={6}
                />
                {error && <div className="ilv-error">{error}</div>}
                <button className="ilv-unlock-btn" type="submit" disabled={loading}>
                  {loading ? 'در حال بررسی...' : '🔓 تأیید و گشودن دعوت‌نامه'}
                </button>
                <button
                  type="button"
                  className="ilv-back-link"
                  onClick={handleResendOtp}
                  disabled={loading}
                >
                  ارسال دوباره‌ی کد
                </button>
                <button
                  type="button"
                  className="ilv-back-link"
                  onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
                  disabled={loading}
                >
                  ← بازگشت
                </button>
              </form>
            </>
          ) : step === 'name' ? (
            <>
              <h1 className="ilv-gate-title">خودت را معرفی کن</h1>
              <p className="ilv-gate-sub">
                شماره‌ات تأیید شد! این دعوت‌نامه برای کسی آماده است که هنوز اسمش نوشته نشده.
                نام و نام خانوادگی‌ات را وارد کن تا دعوت‌نامه به نامت صادر شود.
              </p>
              <form onSubmit={handleNameSubmit} className="ilv-gate-form">
                <input
                  className="ilv-input"
                  placeholder="نام"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  dir="rtl"
                  autoFocus
                />
                <input
                  className="ilv-input"
                  placeholder="نام خانوادگی"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  dir="rtl"
                />
                {error && <div className="ilv-error">{error}</div>}
                <button className="ilv-unlock-btn" type="submit" disabled={loading}>
                  {loading ? 'در حال ثبت...' : '✍️ ثبت نام و گشودن دعوت‌نامه'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="ilv-gate-title">تاریخ تولدت را وارد کن</h1>
              <p className="ilv-gate-sub">
                هنوز تاریخ تولدی برای این شماره ثبت نشده است. برای گشودن دعوت‌نامه،
                تاریخ تولد (شمسی) خودت را وارد کن.
              </p>
              <form onSubmit={handleBirthDateSubmit} className="ilv-gate-form">
                <div className="ilv-birthdate">
                  <p className="ilv-birthdate-label">تاریخ تولد (شمسی)</p>
                  <div className="ilv-birthdate-row" dir="rtl">
                    <select
                      className="ilv-select"
                      value={jd}
                      onChange={(e) => setJd(e.target.value)}
                      aria-label="روز تولد"
                    >
                      <option value="">روز</option>
                      {Array.from({ length: jalaliDaysInMonth(jm) }, (_, i) => i + 1).map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <select
                      className="ilv-select"
                      value={jm}
                      onChange={(e) => { setJm(e.target.value); setJd(''); }}
                      aria-label="ماه تولد"
                    >
                      <option value="">ماه</option>
                      {JALALI_MONTHS.map((name, i) => (
                        <option key={name} value={i + 1}>{name}</option>
                      ))}
                    </select>
                    <select
                      className="ilv-select"
                      value={jy}
                      onChange={(e) => setJy(e.target.value)}
                      aria-label="سال تولد"
                    >
                      <option value="">سال</option>
                      {JALALI_YEARS.map((y) => {
                        const age = CURRENT_JALALI_YEAR - y;
                        let optionColor = '#fa8c16'; // نارنجی (> 25)
                        if (age <= 11) {
                          optionColor = '#ff4d4f'; // قرمز (<= 11)
                        } else if (age <= 25) {
                          optionColor = '#22c55e'; // سبز (11 تا 25)
                        }
                        return (
                          <option key={y} value={y} style={{ color: optionColor }}>
                            {y}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                {exactAge && exactAge.years >= 0 && (
                  <div className="ilv-age-card">
                    <div className="ilv-age-title">✨ سن دقیق شما در ثبت احوال وزارت سحر و جادو ✨</div>
                    <div className="ilv-age-grid">
                      <div className="ilv-age-item">
                        <span className="ilv-age-val">{exactAge.years.toLocaleString('fa-IR')}</span>
                        <span className="ivl-age-lbl">سال</span>
                      </div>
                      <div className="ilv-age-item">
                        <span className="ilv-age-val">{exactAge.months.toLocaleString('fa-IR')}</span>
                        <span className="ivl-age-lbl">ماه</span>
                      </div>
                      <div className="ilv-age-item">
                        <span className="ilv-age-val">{exactAge.days.toLocaleString('fa-IR')}</span>
                        <span className="ivl-age-lbl">روز</span>
                      </div>
                    </div>
                    <p className="ilv-age-note">جغدهای وزارت‌خانه سن دقیق شما را تأیید کرده‌اند.</p>

                    {isNearBirthday && (
                      <div className="ilv-birthday-greeting">
                        {remainingToBirthday.days === 0 ? (
                          <span>🎉 امروز روز تولد شماست! انجمن پاترهدها تولدتان را صمیمانه تبریک می‌گوید. 🎂</span>
                        ) : (
                          <span>🎈 پیشاپیش تولدتان مبارک! انجمن پاترهدها بهترین آرزوها را در این {remainingToBirthday.days.toLocaleString('fa-IR')} روز باقی‌مانده تا تولدتان برای شما دارد. ✨</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {error && <div className="ilv-error">{error}</div>}
                <button className="ilv-unlock-btn" type="submit" disabled={loading}>
                  {loading ? 'در حال گشودن...' : '🔓 گشودن دعوت‌نامه'}
                </button>
              </form>
            </>
          )}
        </div>

        {showConfirmModal && (
          <div className="ilv-modal-overlay">
            <div className="ilv-modal">
              <div className="ilv-modal-icon">⚖️</div>
              <h2 className="ilv-modal-title">تأیید اصالت سن و هویت</h2>
              <p className="ilv-modal-text">
                آیا مطمئن هستید که تاریخ تولد خود را درست وارد کرده‌اید؟
              </p>
              <p className="ilv-modal-warning">
                ⚠️ هشدار: در صورتی که تاریخ تولد خود را اشتباه اعلام کنید، طبق قوانین ویزنگاموت، شامل جریمه و پیگرد قانونی از سمت وزارت سحر و جادو خواهید شد.
              </p>
              <div className="ilv-modal-actions">
                <button className="ilv-modal-btn confirm" onClick={performBirthDateSubmit}>
                  بله، کاملاً مطمئنم
                </button>
                <button className="ilv-modal-btn cancel" onClick={() => setShowConfirmModal(false)}>
                  خیر، ویرایش تاریخ
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Letter reveal ──────────────────────────────────────────
  const fullName = `${inv.recipientFirstName} ${inv.recipientLastName}`;
  const addressLines = [inv.addressLine1, inv.addressLine2, inv.addressLine3].filter(Boolean);
  const departure = formatDeparture(inv.departureDate);

  return (
    <div className="ilv-root">
      <div className="ilv-stars" aria-hidden="true">
        {Array.from({ length: 40 }).map((_, i) => (
          <span key={i} style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, animationDelay: `${(Math.random() * 4).toFixed(1)}s` }} />
        ))}
      </div>

      {!opened ? (
        <div className="ilv-envelope" onClick={() => setOpened(true)} role="button" title="برای گشودن کلیک کنید">
          <div className="ilv-env-flap" />
          <div className="ilv-env-body">
            <div className="ilv-env-address">
              <strong>{fullName}</strong>
              {addressLines.map((l, i) => <span key={i}>{l}</span>)}
            </div>
            <div className="ilv-wax">H</div>
          </div>
          <p className="ilv-env-hint">✉️ برای گشودن نامه کلیک کنید</p>
        </div>
      ) : (
        <div className="ilv-letters">
          {/* هنوز ۱۱ سالت نشده؟ — طبق تصمیم محصول، این پیام فقط در این صفحه نمایش داده
              می‌شود؛ نامه‌ی عذرخواهی (برای بالای ۱۱ سال) عمداً اینجا نشان داده نمی‌شود. */}
          {inv.under11Notice && (
            <div className="ilv-parchment ilv-fade" style={{ textAlign: 'center' }}>
              <p className="ilv-body">🦉 {inv.under11Notice}</p>
            </div>
          )}

          {/* Acceptance letter */}
          <div className="ilv-parchment ilv-fade">
            <div className="ilv-crest">🦁 🦅 🦡 🐍</div>
            <h2 className="ilv-school">مدرسه‌ی علوم و فنون جادوگری هاگوارتز</h2>
            <p className="ilv-headmaster">مدیر: آلبوس دامبلدور<br /><small>(نشان مرلین، درجه‌ی یک)</small></p>
            <div className="ilv-body">
              <p>{fullName} عزیز،</p>
              {inv.referredByName ? (
                <div style={{
                  background: 'rgba(212,175,55,0.08)',
                  borderRight: '4px solid #c49510',
                  padding: '12px 16px',
                  borderRadius: '0 8px 8px 0',
                  marginBottom: '20px',
                  fontSize: '0.92rem',
                  lineHeight: '1.7',
                  textAlign: 'justify'
                }}>
                  ✨ این دعوت‌نامه به پیشنهاد و معرفی دوست شما، <strong>{inv.referredByName}</strong>، برای شما فرستاده شده است تا سفر جادویی خود را با هم آغاز کنید!
                  در این ماجراجویی، شما می‌توانید با ثبت‌نام و دریافت بلیت قطار هاگوارتز اکسپرس، از سکوی ۹ و ۳/۴ عبور کرده و پس از ورود به قلعه، در کلاه‌ گروه‌بندی خانه‌تان حضور یابید، چوبدستی جادویی‌تان را دریافت کنید و پاترونوس منحصربه‌فرد خود را کشف کنید.
                </div>
              ) : (
                <p>
                  با کمال مسرت به اطلاع می‌رسانیم که جای شما در مدرسه‌ی علوم و فنون جادوگری هاگوارتز
                  محفوظ است. فهرست کتاب‌ها و وسایل مورد نیاز به‌زودی به دست شما خواهد رسید.
                </p>
              )}
              <p>
                قطار هاگوارتز اکسپرس در تاریخ <strong>{departure}</strong> رأس ساعت
                <strong> {inv.departureTime} </strong> از سکوی <strong>{inv.platform}</strong> حرکت خواهد کرد.
                لطفاً پیش از حرکت قطار در ایستگاه حاضر شوید.
              </p>
            </div>
            <p className="ilv-sign">ارادتمند شما،<br />مینروا مک‌گونگال<br /><small>معاون مدرسه</small></p>
            <div className="ilv-regcode">
              کد ثبت‌نام: <code>{inv.code}</code>
              <span>(هنگام ثبت‌نام وارد کنید)</span>
            </div>
            {/* قبلاً ثبت‌نام کرده و فقط می‌خواهد وارد سایت شود — لینک مستقیم به
                دروازه‌ی ورود (پلتفرم ۹ و ۳/۴)، جدا از دکمه‌ی «ادامه ثبت‌نام» پایین صفحه */}
            <a className="ilv-portal-link" href="/portal-9-3-4">
              🚪 قبلاً ثبت‌نام کرده‌اید؟ ورود به سایت
            </a>
          </div>

          {/* Supplies list — دقیقاً همان اقلامی که در پنل مدیریت (تب «وسایل مدرسه») ثبت شده */}
          {inv.requirementCategories && inv.requirementCategories.length > 0 && (
            <div className="ilv-parchment ilv-fade ilv-supplies">
              <div className="ilv-crest">🦁 🦅 🦡 🐍</div>
              <h2 className="ilv-school">مدرسه‌ی علوم و فنون جادوگری هاگوارتز</h2>
              <p className="ilv-headmaster">
                به مدیریت آلبوس دامبلدور<br />
                <small>(نشان مرلین درجه‌ی یک، رئیس انجمن بین‌المللی جادوگران، جادوگر ارشد ویزنگاموت)</small>
              </p>
              <div className="ilv-body">
                <p className="ilv-supplies-intro">اقلام مورد نیاز دانش‌آموزان سال اول:</p>

                {inv.requirementCategories.map((cat) => (
                  <div key={cat.category} className="ilv-supply-cat">
                    <h3 className="ilv-supply-cat-title">{cat.label}</h3>
                    <ol className="ilv-supply-list">
                      {cat.items.map((item) => (
                        <li key={item._id} className="ilv-supply-item">
                          <span className="ilv-supply-name">
                            {item.farsiName || item.name}
                            {item.isMandatory && <span className="ilv-supply-mandatory">اجباری</span>}
                          </span>
                          {(item.farsiDescription || item.description) && (
                            <span className="ilv-supply-desc"> — {item.farsiDescription || item.description}</span>
                          )}
                          {(item.priceGalleons > 0 || item.priceSickles > 0 || item.priceKnuts > 0) && (
                            <span className="ilv-supply-price">
                              {[
                                item.priceGalleons > 0 ? `${item.priceGalleons.toLocaleString('fa-IR')} گالیون` : null,
                                item.priceSickles > 0 ? `${item.priceSickles.toLocaleString('fa-IR')} سیکل` : null,
                                item.priceKnuts > 0 ? `${item.priceKnuts.toLocaleString('fa-IR')} نات` : null
                              ].filter(Boolean).join(' و ')}
                            </span>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}

                <p className="ilv-supplies-note">
                  توجه داشته باشید که تمام کتاب‌های درسی و لباس‌ها باید با نام و نام خانوادگی دانش‌آموز نشان‌گذاری شوند.
                </p>
                <p className="ilv-supplies-note">
                  هر دانش‌آموز می‌تواند صاحب یک جغد، یک گربه یا یک وزغ باشد.
                </p>
                <p className="ilv-supplies-note">
                  لازم به ذکر است که دانش‌آموزان سال اول اجازه‌ی داشتن جاروی پرنده همراه را ندارند.
                </p>
              </div>
              <p className="ilv-sign" style={{ fontSize: 13 }}>تمامی وسایل را می‌توانید از فروشگاه‌های کوچه دیاگون تهیه کنید.</p>
            </div>
          )}

          {/* Train ticket */}
          <div className="ilv-ticket ilv-fade">
            <div className="ilv-ticket-head">
              <span>🚂 هاگوارتز اکسپرس</span>
              <span className="ilv-ticket-oneway">بلیت یک‌طرفه</span>
            </div>
            <div className="ilv-ticket-route">لندن ← هاگوارتز</div>
            <div className="ilv-ticket-grid">
              <div><small>مسافر</small><strong>{fullName}</strong></div>
              <div><small>سکو</small><strong>{inv.platform}</strong></div>
              <div><small>تاریخ حرکت</small><strong>{departure}</strong></div>
              <div><small>ساعت</small><strong>{inv.departureTime}</strong></div>
            </div>
            <div className="ilv-ticket-foot">
              <span>شماره بلیت</span>
              <code>{inv.ticketCode}</code>
            </div>
          </div>

          {/* ادامه‌ی ثبت‌نام — چون کد، شماره و نام از همین‌جا مشخص است،
              کاربر در صفحه‌ی بعد دیگر مجبور به تایپ دوباره‌ی آن‌ها نیست */}
          <a
            className="ilv-unlock-btn"
            style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: '1.5rem' }}
            href={`/journey?code=${encodeURIComponent(inv.code)}&phone=${encodeURIComponent(phone)}&firstName=${encodeURIComponent(inv.recipientFirstName || '')}&lastName=${encodeURIComponent(inv.recipientLastName || '')}`}
          >
            🪄 ادامه‌ی ثبت‌نام و ورود به دنیای جادو
          </a>
        </div>
      )}
    </div>
  );
}
